import { codiceValido } from './services/colataService';

// Generazione del codice a barre in SVG (vettoriale). Usata dal componente BarcodeCodice,
// dal cartellino e dal PDF della scheda articolo.
// Formato attuale: Code 128 (accetta lettere, numeri e trattino). Il QR si aggiungerà come altro formato.
// La libreria JsBarcode si carica SOLO quando serve (import dinamico).

export const FORMATO_PREDEFINITO = 'code128';

// Formati letti dalla fotocamera (nomi di Html5QrcodeSupportedFormats): Code 128 e QR per i codici Colata,
// più EAN-13 e Code 39 per le etichette dei fornitori (campi SAP / codice fornitore).
export const FORMATI_SCANNER = ['CODE_128', 'QR_CODE', 'CODE_39', 'EAN_13'];

const FORMATI = {
  code128: { jsbarcode: 'CODE128' },
};

const cache = new Map(); // "formato|codice" -> markup SVG

/**
 * Genera il markup SVG del codice a barre di `codice` (o lancia un errore se non valido / formato sconosciuto).
 * Il risultato è in cache. Margini laterali ampi (zona di silenzio) e testo leggibile sotto le barre.
 */
export const generaSvgBarcode = async (codice, formato = FORMATO_PREDEFINITO) => {
  const def = FORMATI[formato];
  if (!def) throw new Error(`Formato non supportato: ${formato}`);
  const valore = String(codice ?? '').trim();
  if (!codiceValido(valore)) throw new Error('Codice non valido');
  const chiave = `${formato}|${valore}`;
  if (cache.has(chiave)) return cache.get(chiave);

  const { default: JsBarcode } = await import('jsbarcode');
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, valore, {
    format: def.jsbarcode,
    width: 2,
    height: 72,
    displayValue: true, // codice leggibile sotto le barre
    font: 'Arial, Helvetica, sans-serif',
    fontSize: 20,
    textMargin: 3,
    marginTop: 6,
    marginBottom: 6,
    marginLeft: 20,   // zona di silenzio >= 10 moduli (modulo = 2)
    marginRight: 20,
    background: '#ffffff',
    lineColor: '#000000',
  });

  // JsBarcode imposta già il viewBox: si tolgono le dimensioni fisse così l'SVG si adatta al contenitore
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  const markup = svg.outerHTML;
  cache.set(chiave, markup);
  return markup;
};

/**
 * Converte il markup SVG in un'immagine PNG nitida (per il PDF).
 * Ritorna { dataUrl, rapporto } con rapporto = altezza / larghezza.
 */
export const svgInPng = (markup, larghezzaPx = 1400) => new Promise((resolve, reject) => {
  const documento = new DOMParser().parseFromString(markup, 'image/svg+xml');
  const svg = documento.documentElement;
  const vb = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number);
  const [, , w, h] = vb;
  if (!w || !h) {
    reject(new Error('SVG senza viewBox'));
    return;
  }
  const altezzaPx = Math.round((larghezzaPx * h) / w);
  svg.setAttribute('width', String(larghezzaPx));
  svg.setAttribute('height', String(altezzaPx));

  const xml = new XMLSerializer().serializeToString(svg);
  const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = larghezzaPx;
    canvas.height = altezzaPx;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, larghezzaPx, altezzaPx);
    ctx.drawImage(img, 0, 0, larghezzaPx, altezzaPx);
    URL.revokeObjectURL(url);
    resolve({ dataUrl: canvas.toDataURL('image/png'), rapporto: altezzaPx / larghezzaPx });
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Impossibile creare l\'immagine del codice a barre'));
  };
  img.src = url;
});
